import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../lib/i18n';
import { Cookie } from 'lucide-react';

const STORAGE_KEY = 'ofa_cookie_consent';

/**
 * CNIL/GDPR-compliant cookie banner.
 * Works in tandem with Google Consent Mode v2 declared in /public/index.html:
 *   - Default consent is DENIED for analytics (set before GA4 fires)
 *   - When the visitor clicks "Accept", we update consent to GRANTED so GA4 can track
 *   - When the visitor clicks "Decline", we persist that choice and GA4 stays muted
 *
 * The choice is stored in localStorage so the banner only shows once per device.
 */
export const CookieBanner = () => {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const updateConsent = (granted) => {
    try {
      localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied');
    } catch {}
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: granted ? 'granted' : 'denied',
      });
    }
    setVisible(false);
  };

  if (!visible) return null;

  const isFR = lang === 'fr';

  return (
    <div
      role="dialog"
      aria-label={isFR ? 'Préférences cookies' : 'Cookie preferences'}
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex-shrink-0 hidden sm:block">
          <div className="w-10 h-10 rounded-full bg-[#F8CD46]/15 flex items-center justify-center">
            <Cookie className="w-5 h-5 text-[#14274B]" />
          </div>
        </div>
        <div className="flex-1 text-sm text-slate-600 leading-relaxed">
          {isFR ? (
            <>
              Nous utilisons des cookies de mesure d'audience (Google Analytics) pour comprendre comment vous utilisez notre site et l'améliorer. Aucun cookie n'est déposé sans votre accord.{' '}
              <Link to="/privacy" className="text-[#2D78F6] hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                En savoir plus
              </Link>
            </>
          ) : (
            <>
              We use analytics cookies (Google Analytics) to understand how you use our site and improve it. No cookies are set without your consent.{' '}
              <Link to="/privacy" className="text-[#2D78F6] hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                Learn more
              </Link>
            </>
          )}
        </div>
        <div className="flex gap-2 w-full md:w-auto flex-shrink-0">
          <button
            onClick={() => updateConsent(false)}
            data-testid="cookie-decline-btn"
            className="flex-1 md:flex-none px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors whitespace-nowrap"
          >
            {isFR ? 'Refuser' : 'Decline'}
          </button>
          <button
            onClick={() => updateConsent(true)}
            data-testid="cookie-accept-btn"
            className="flex-1 md:flex-none px-5 py-2 text-sm font-semibold text-white bg-[#2D78F6] hover:bg-[#1A5BB5] rounded-md transition-colors whitespace-nowrap"
          >
            {isFR ? 'Tout accepter' : 'Accept all'}
          </button>
        </div>
      </div>
    </div>
  );
};
